import { z } from 'zod';

import {
  getSingleBillCurrencyCodeByCountry,
  getSingleBillPaymentElementConfigurationBucket,
  type AvailableCurrencies,
  type SingleBill,
} from '@ctct/domain-layer';

import type { TranslationGetter } from '../../../types';
import type { ValidationService } from '../../_shared/Form/services/ValidationService';
import type { SingleBillInfoFormPageStore, SingleBillInfoFormValues } from '../types';

export class SingleBillInfoFormService {
  private readonly dayOfMonthOptions: { text: string; key: string; value: string }[];

  constructor(
    private singleBill: SingleBill,
    private validationService: ValidationService,
    private availableCurrencies: AvailableCurrencies,
    private pageStore: SingleBillInfoFormPageStore,
    private isPartnerProvisionAccountsToStripe: boolean,
    private t: TranslationGetter
  ) {
    const maxDay = isPartnerProvisionAccountsToStripe ? 31 : 28;

    this.dayOfMonthOptions = Array.from({ length: maxDay }, (_, index) => {
      const day = index + 1;

      return {
        text: day.toString(),
        key: day.toString(),
        value: day.toString(),
      };
    });
  }

  public api() {
    return {
      validate: (value: SingleBillInfoFormValues) => this.validate(value),
    };
  }

  public data() {
    return {
      getDayOfMonthOptions: () => {
        return this.dayOfMonthOptions;
      },

      isEditing: () => {
        return this.isEditing();
      },

      getZodSchema: () => this.getZodSchema(),

      getCurrencyOptions: () =>
        this.availableCurrencies.getCurrencyOptions().map((currency) => ({
          text: currency.value,
          value: currency.value,
          key: currency.value,
        })),

      shouldDisableCountry: () => false,
    };
  }

  public handlers() {
    return {
      onBillingCountryChange: (newCountryValue: string): boolean => {
        const newCountry = newCountryValue || null;
        const state = this.pageStore.getState();
        // After activation the billing currency is locked, so a country change must not
        // change it. Payment methods are country-driven, so we still recompute the
        // payment element bucket from the new country and the locked currency.
        const newCurrency = this.isEditing()
          ? state.selectedBillingCurrency
          : getSingleBillCurrencyCodeByCountry(newCountry) || state.selectedBillingCurrency;
        const newConfigurationBucket = getSingleBillPaymentElementConfigurationBucket(newCountry, newCurrency);
        const willReloadPaymentElement = newConfigurationBucket !== state.paymentElementConfigurationBucket;

        if (willReloadPaymentElement && state.hasInteractedWithPaymentElement) {
          const shouldContinue = window.confirm(
            this.t('application-layer.singlebill-page.payment-element-reload-confirmation')
          );

          if (!shouldContinue) {
            return false;
          }
        }

        this.pageStore.setState({
          selectedBillingCountry: newCountry,
          selectedBillingCurrency: newCurrency,
          paymentElementConfigurationBucket: newConfigurationBucket,
          hasInteractedWithPaymentElement: !willReloadPaymentElement && state.hasInteractedWithPaymentElement,
        });

        return true;
      },

      onCurrencyChange: (selectedBillingCurrency: string) => {
        const state = this.pageStore.getState();
        const paymentElementConfigurationBucket = getSingleBillPaymentElementConfigurationBucket(
          state.selectedBillingCountry,
          selectedBillingCurrency
        );
        const willReloadPaymentElement = paymentElementConfigurationBucket !== state.paymentElementConfigurationBucket;

        if (willReloadPaymentElement && state.hasInteractedWithPaymentElement) {
          const shouldContinue = window.confirm(
            this.t('application-layer.singlebill-page.payment-element-reload-confirmation')
          );

          if (!shouldContinue) {
            return false;
          }
        }

        this.pageStore.setState({
          selectedBillingCurrency,
          paymentElementConfigurationBucket,
          hasInteractedWithPaymentElement: !willReloadPaymentElement && state.hasInteractedWithPaymentElement,
        });

        return true;
      },
    };
  }

  public isEditing() {
    return this.singleBill.getIsSingleBillActivated() && this.singleBill.getIsSingleBillEnabled();
  }

  public validate = (value: SingleBillInfoFormValues) => this.getZodSchema().safeParse(value).success;

  private getZodSchema() {
    const schema = z
      .object({
        firstname: this.validationService.getFirstNameValidationSchema(),
        lastname: this.validationService.getLastNameValidationSchema(),
        companyname: this.validationService.getCompanyNameValidationSchema(),
        address1: this.validationService.getAddress1ValidationSchema(),
        address2: this.validationService.getAddress2ValidationSchema(),
        city: this.validationService.getCityValidationSchema(),
        zipcode: this.validationService.getZipCodeValidationSchema(),
        state: this.validationService.getStateValidationSchema(),
        country: this.validationService.getCountryValidationSchema(),
        phone: this.validationService.getPhoneValidationSchema(),
        email: this.validationService.getEmailValidationSchema(),
        currentTaxAgreement: this.validationService.getCurrentTaxAgreementSchema(),
        vatNumber: this.validationService.getVatValidationSchema(),
        partnerContactName: this.getPartnerContactNameValidationSchema(),
        partnerContactEmail: this.validationService.getEmailValidationSchema(),
      })
      .superRefine(({ country, state, zipcode, currentTaxAgreement = null, vatNumber }, ctx) => {
        this.validationService.validateZipCode(zipcode, country, ctx);
        this.validationService.validateVatNumbers(country, currentTaxAgreement, vatNumber, ctx);
        this.validationService.validateState(country, state, ctx);
      });

    return schema;
  }

  private getPartnerContactNameValidationSchema() {
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 16;

    return this.validationService.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }
}
